import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as JSZip from 'jszip';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import {Observable, firstValueFrom, of, lastValueFrom} from 'rxjs';
import { GlobalSelectionControlService } from '../map/services/global-selection-control.service';
import { Project, ProjectUploadModel } from '../models/project';
import { ConfigurationTerrain } from '../models/terrains';
import { DynamicTable } from '../popup/dynamic-table/dynamic-table.component';
import { ConfigurationService } from './configuration.service';
import { EndpointsService } from './endpoints.service';
import { ModalControlService } from './modal-control.service';
import { RouteSegmentsService } from './route-segments.service';
import { UiBlockService } from './ui-block.service';
import { UserSettingsService } from './userSettings.service';
import { UtilsService } from './utils.service';
import { UserSettingsDTO } from '../models/UserSettings';
import { cloneDeep } from 'lodash';
import { UserSpaceLimitWarningMessage } from '../models/error';
import {GritApiService} from "./grit/grit.api.service";

@Injectable({
  providedIn: 'root'
})
export class ProjectService {

  public editionMode:boolean = false;
  public projects : Project[] = [];
  public activeProject: Project;
  public selectedProject: Project;
  public importMode : boolean = false;

  constructor(private http: HttpClient,
    private endpointService: EndpointsService,
    private uiBlock: UiBlockService,
    private modalService: ModalControlService,
    private configService: ConfigurationService,
    private routeSegmentService : RouteSegmentsService,
    private userSettingsService: UserSettingsService,
    private popupService: BsModalService,
    private utilsService: UtilsService,
    private gritApiService :GritApiService,
    ) {
      //this.selectedProject = new Project();
      this.userSettingsService.updateSettings = this.updateUserSettings.bind(this);
     }

  getAllProjects() : void {
    this.getProjects().subscribe(projects => {
      this.activeProject = projects.find(x => x.active);
      this.routeSegmentService.loadState(this.activeProject.id);
      this.configService.loadConfigurationsOfProject(this.activeProject.id);
      this.projects = projects;
      if (this.selectedProject == undefined){
        this.selectProject(this.activeProject);
        this.selectedProject = this.activeProject;
      }else {
        this.selectProject(this.selectedProject);
      }
    });
  }
  updateUserSettings(){
    let userSettingsDTO : UserSettingsDTO = new UserSettingsDTO();
    userSettingsDTO.id = this.activeProject.id;
    userSettingsDTO.usersettingsstr = JSON.stringify(this.userSettingsService.userSettings);
    this.activeProject.userSettingsJSON = userSettingsDTO.usersettingsstr;
    this.http.post(this.endpointService.endpointsUserSettingsUpdate,
      userSettingsDTO,
      this.endpointService.httpOptions
    ).subscribe((result) =>
    {
    })
  }
  getProjects(): Observable<Project[]> {
    // return this.http.get<Project[]>(
    //   this.endpointService.apiUrlProjects,
    //   this.endpointService.httpOptions
    // );

    return this.http.get<Project[]>(
      this.endpointService.gritProject + "/all",
      this.endpointService.httpOptions
    );

  }

  async getProject(projectId: number): Promise<Project> {
    return firstValueFrom(this.http.get<Project>(
      this.endpointService.apiUrlProjects + `/${projectId}`,
      this.endpointService.httpOptions
    ));
  }

  getProjectsPromise() : Promise<Project[]>{
    return this.http.get<Project[]>(
      this.endpointService.apiUrlProjects,
      this.endpointService.httpOptions
    ).toPromise();

    // return lastValueFrom(this.getProjects());
  }
  deleteProject(projectId: number): Observable<boolean> {
    return this.http
    .delete<boolean>(
      this.endpointService.apiUrlProjects + '/' + projectId,
      this.endpointService.httpOptions
    );
  }

  deleteProjectCalculations(projectId: number){
    return this.http
    .delete<boolean>(
      this.endpointService.deleteCalculationsByProject + '/' + projectId,
      this.endpointService.httpOptions
    );
  }


  saveProject(project: Project, gritOriginator: string) : void{
    this.uiBlock.startBlock("Saving project...");
      if (project.id == null) {
        this.http
          .post(
            this.endpointService.apiUrlProjects,
            project,
            this.endpointService.httpOptions
          )
          .subscribe({
            next: (project : Project) => {
              //#region GRIT
              this.gritApiService.interferenceTestCreate(project.id, gritOriginator).then(
                (interferenceTests) => {
                    this.modalService.showMesssageModal("Project created", "The project was saved successfully!");
                    this.getAllProjects();
                  },
                  (error) => console.log(error)
                )
                .finally(() => this.uiBlock.stopBlock());
              //endregion
            },
            error: (error: any) => {
              this.modalService.showMesssageModal("Error", error.error.message);
              this.uiBlock.stopBlock();
            }
          });
      }
      else {
        this.http
          .put(
            this.endpointService.apiUrlProjects,
            project,
            this.endpointService.httpOptions
          )
          .subscribe(
            (data) => {
              if(data == true){
                this.modalService.showMesssageModal("Project saved", "The project was saved successfully!");
                this.getAllProjects();
                this.uiBlock.stopBlock();
              }
            },
            (error) => {
              this.uiBlock.stopBlock();
              if (error && error.status == 401) {
                this.modalService.showMesssageModal(
                  'Error Updating',
                  'You can not edit the Default Project.'
                )
                return;
              }
              this.modalService.showMesssageModal("Error","Error updating the project!");
            }
          );
      }

    }

  selectProject(project: Project) : void{
    this.projects.forEach(proj => {
      proj.selected = false;
      if(proj.id == project.id){
        proj.selected = true;
        this.selectedProject = proj;
      }
    });
  }

  activateProject(project: Project) : Observable<Project>{
    return this.http
    .put<Project>(
      this.endpointService.apiUrlActivateProject + '/' + project.id,
      project,
      this.endpointService.httpOptions
      );
  }

  projectEditable(project: Project = null, showMessage: boolean = true) : boolean {
    if (project == null){
      project = this.activeProject;
    }
    if(!project.editable && !UtilsService.getIsAdminFromJwtToken(this.endpointService.getUserRolesToken()) ){
      if (showMessage){
        this.modalService.showMesssageModal("Error",'Default Project is not editable.');
      }
      return false;
    }else {
      return true;
    }

  }

  async importProject(project: ProjectUploadModel){
    let terrainFileContent: string = null;
    let importedProjectName: string = null;
    const projectName = project.name;
    const projectDescription = project.description;
    //get terrain file
    await JSZip.loadAsync(project.zipFile).then(async zip => {
      let terrainsLoaded: boolean = false;
      let projectNameLoaded: boolean = false;
      for(let [filename, file] of Object.entries(zip.files)) {
          if (filename.toLowerCase().includes("terrains")){
            await zip.files[filename].async('string').then(fileData => {
              terrainFileContent = fileData;
              terrainsLoaded = true;
            })
          }
          if (filename.toLowerCase().includes("projectname")){
            await zip.files[filename].async('string').then(fileData => {
              importedProjectName = fileData;
              projectNameLoaded = true;
            })
          }
          if (terrainsLoaded && projectNameLoaded){
            break;
          }
      }
    })
    // for some reason proj name and descr get deleted on the above function
    project.name = projectName;
    project.description = projectDescription;
    let terrains: ConfigurationTerrain[] ;
    if (terrainFileContent != null){
      terrains = JSON.parse(terrainFileContent).terrains as ConfigurationTerrain[];
    }

    let allTerrains: ConfigurationTerrain[] = await this.configService.fetchAllTerrains().toPromise();
    if (!allTerrains || allTerrains.length == 0){
      await this.configService.fetchTerrainsByProjectPromiseRequest(this.activeProject.id);
      allTerrains = cloneDeep(this.configService.savedTerrains$.getValue());
    }
    const canBeImportedTerrains: ConfigurationTerrain[] = [];
    let defaultGTOPOTerrainAdded = false;
    let defaultDTED1TerrainAdded = false;
    const mismatchedTerrains: string[] = [];
    for (const terrain of terrains){
      const toBeImported = allTerrains.filter(savedTerrain => savedTerrain.terrainTypeId == terrain.terrainTypeId && savedTerrain.name == terrain.name);
      const isGTOPODefaultTerrain: boolean = terrain.name == ConfigurationTerrain.DEFAULT_GTOPO30;
      const isDTED1DefaultTerrain: boolean = terrain.name == ConfigurationTerrain.DEFAULT_TERRAIN_NAME;
      if (toBeImported.length === 0) {
        mismatchedTerrains.push(terrain.name);
        continue;
      }
      for (const element of toBeImported){
        const dontAdd = (isGTOPODefaultTerrain && defaultGTOPOTerrainAdded) || (isDTED1DefaultTerrain && defaultDTED1TerrainAdded)
        if (dontAdd){
          break;
        }
        element.position = terrain.position;
        element.active = terrain.active;
        if (isGTOPODefaultTerrain){
          defaultGTOPOTerrainAdded = true;
          element.projectName = "Default Terrain";
        } else if (isDTED1DefaultTerrain){
          defaultDTED1TerrainAdded = true;
          element.projectName = "Default Terrain";
        } else {
          const terrainProject = this.projects.find(p => p.id == element.projectId);
          if (!terrainProject){
            continue;
          }
          element.projectName = terrainProject.name;
        }

        canBeImportedTerrains.push(element);

      }

    }
    if (mismatchedTerrains.length > 0) {
      this.modalService.showMesssageModal(
        "Warning",
        "The following terrains from the imported project do not exist in Demeter Web: " + mismatchedTerrains.join(', ')
      );
    }
    if (canBeImportedTerrains.length > 2){
      // open popup to select terrains to be imported
      const bsModalRef : BsModalRef = this.popupService.show(DynamicTable, {
        backdrop: 'static' // Prevent closing on backdrop click
      });
      bsModalRef.content.entity = "Terrains";
      bsModalRef.content.type = "Terrain Select";
      bsModalRef.content.selectAllActive = false;
      bsModalRef.content.dataTerrains = canBeImportedTerrains;
      bsModalRef.content.displayedColumns = this.utilsService.getDynamicTableColumns("Terrains");
      bsModalRef.content.wasCanceled.subscribe(wasCanceled => {
        if (wasCanceled){
          return;
        }
        bsModalRef.content.onClose.subscribe(result => {
            this.proceedImport(project, result);
        });
      });
    } else {
      this.proceedImport(project, canBeImportedTerrains, false);
    }


  }

  private proceedImport(project: ProjectUploadModel, terrains: any[], needMapping = true){
    this.uiBlock.startBlockWaitForProgress('Parsing XML files...');
    if (terrains!=null && terrains.length > 0 ){
      project.terrains = this.cleanTerrainsList(terrains, needMapping);
    }
    const formData: FormData = ProjectUploadModel.mapToFormData(project);
    this.http.post<void>(
      this.endpointService.endpointUploadProject,
      formData,
      this.endpointService.httpOptions
    ).subscribe({
      error: error => {
        if (error && error.status == 504){
          return;
        }
        if (error && error.status == 409){
          this.modalService.showMesssageModal("Error","Error importing project. Project Name already exists.");
        } else if (error && error.status == 428){
          this.modalService.showMesssageModal("Error","Error importing project. User is already importing a project");
        }
        else if(error && error.status == 507){
          this.modalService.showMesssageModal("Warning",UserSpaceLimitWarningMessage);
        }
        else {
        this.modalService.showMesssageModal("Error","Error importing project. Please make sure the ZIP file is valid.")
        }
        this.uiBlock.stopBlockWaitForProgress();
      }
    })
  }

  private cleanTerrainsList(uncleanTerrains, needMapping: boolean): ConfigurationTerrain[]{
    const toReturn: ConfigurationTerrain[] = [];
    for (const unmapped of uncleanTerrains){
      if (needMapping){
        const terrain: ConfigurationTerrain = new ConfigurationTerrain();
        terrain.id = unmapped.id;
        terrain.name = unmapped.TerrainName;
        terrain.terrainTypeId = unmapped.terrainTypeId;
        terrain.active =unmapped.active;
        terrain.position = unmapped.position;
        //terrain.projectId = newProjectId;
        terrain.path = unmapped.path;
        toReturn.push(terrain);
      } else {
        //unmapped.projectId = newProjectId;
        toReturn.push(unmapped);
      }
    }
    toReturn.sort((a, b) => (a.position > b.position) ? 1 : ((b.position > a.position) ? -1 : 0));
    toReturn[0].position = 1;
    for (let i = 1; i<toReturn.length; i++){
      toReturn[i].position = toReturn[i-1].position + 1;
    }
    return toReturn;
  }
}
